import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { nexusApi } from '../api/nexus';
import { userManagementApi } from '../api/userManagement';
import * as contentStudioApi from '../api/contentStudioV2';
import { isProductionHost } from '../utils/environment';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import LanguageToggle from '../components/LanguageToggle';
import DemoBanner from '../components/DemoBanner';
import NexusHeader from '../components/NexusHeader';
import InboxIconHeader from '../components/InboxIconHeader';
import GridTile from '../components/common/GridTile';
import ExecutorPerformanceTracker from '../components/dashboard/ExecutorPerformanceTracker';
import VideoApprovalComparison from '../components/dashboard/VideoApprovalComparison';
import VideoMonthlyTrend from '../components/dashboard/VideoMonthlyTrend';
import { EMZI_COLORS, EMZI_SPACING, EMZI_RADIUS, EMZI_SHADOWS } from '../styles/emziCI';
import { txt } from '../utils/bilingualText';

// ============================================================
// SUPERVISOR DASHBOARD CONTENT - New Redesigned Component
// ============================================================
const SupervisorDashboardContent = ({ user, approvalQueue, loadingQueue, handleApprovalAction, navigate, icons, t }) => {
  const { token } = useAuth();
  const [supervisorStats, setSupervisorStats] = useState(null);
  const [videoQueue, setVideoQueue] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [allSubmissions, setAllSubmissions] = useState([]);
  
  // Modal states
  const [activeModal, setActiveModal] = useState(null); // 'pending_video', 'request_edits', 'production_complete', 'awaiting_video'
  const [modalData, setModalData] = useState([]);
  
  // Get user's team_ids from their group_roles. A supervisor can belong to
  // multiple functional groups, so we expose ALL of them here and let the
  // user pick a single group OR view aggregated data across all their groups.
  const getUserGroups = () => {
    const groupRoles = user?.group_roles || user?.groups || [];
    const names = [];
    for (const g of groupRoles) {
      if (typeof g === 'object' && g) {
        const n = g.group_name || g.name;
        if (n && !names.includes(n)) names.push(n);
      } else if (typeof g === 'string' && g && !names.includes(g)) {
        names.push(g);
      }
    }
    return names;
  };
  const userGroups = getUserGroups();
  // 'ALL' sentinel = aggregated scope across every group the user manages.
  const [selectedGroup, setSelectedGroup] = useState(userGroups.length > 1 ? 'ALL' : (userGroups[0] || 'Content'));

  // Resolve the team_id query param sent to backend: comma-separated when ALL.
  const getUserTeamId = () => {
    if (selectedGroup === 'ALL') {
      return userGroups.length ? userGroups.join(',') : 'Content';
    }
    return selectedGroup || (userGroups[0] || 'Content');
  };

  const loadSupervisorData = useCallback(async () => {
    try {
      setLoadingStats(true);
      const teamId = getUserTeamId();
      
      const [statsRes, videoRes, queueRes] = await Promise.all([
        contentStudioApi.getSupervisorStats(token, teamId),
        contentStudioApi.getVideoQueue(token, teamId),
        contentStudioApi.getSubmissionsQueue(token, teamId)
      ]);
      setSupervisorStats(statsRes);
      setVideoQueue(videoRes?.videos || []);
      setAllSubmissions(queueRes?.submissions || []);
    } catch (error) {
      console.error('Failed to load supervisor stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [token, selectedGroup]);

  useEffect(() => {
    loadSupervisorData();
  }, [loadSupervisorData]);

  // ===== SOA Overview state & loader =====
  const [soaOverview, setSoaOverview] = useState(null);
  const [soaLoading, setSoaLoading] = useState(false);
  const [soaExpanded, setSoaExpanded] = useState(false);
  const [soaSelectedUser, setSoaSelectedUser] = useState(null);
  const todayStr = new Date().toISOString().split('T')[0];
  const sevenAgoStr = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  const [soaStart, setSoaStart] = useState(sevenAgoStr);
  const [soaEnd, setSoaEnd] = useState(todayStr);

  const loadSoaOverview = useCallback(async () => {
    try {
      setSoaLoading(true);
      const teamId = getUserTeamId();
      const qs = new URLSearchParams({ team_id: teamId, start_date: soaStart, end_date: soaEnd }).toString();
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v2/content/supervisor/soa-overview?${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) setSoaOverview(await r.json());
    } catch (e) { console.error('SOA overview load failed:', e); }
    setSoaLoading(false);
  }, [token, soaStart, soaEnd, selectedGroup]);

  useEffect(() => {
    // Auto-load ONLY when first expanding — subsequent date changes use the Apply button
    // to avoid firing a request on every keystroke in the date picker.
    if (soaExpanded && !soaOverview) loadSoaOverview();
  }, [soaExpanded]);// eslint-disable-line

  // Invalidate SOA overview when supervisor switches groups so the next
  // expand (or the manual Apply button) re-fetches scoped to the new selection.
  useEffect(() => {
    setSoaOverview(null);
  }, [selectedGroup]);

  const handleVideoAction = async (sessionId, action, comment = '') => {
    try {
      await contentStudioApi.approveVideo(token, sessionId, action, comment);
      alert(`✅ Video ${action}d successfully!`);
      loadSupervisorData();
      setActiveModal(null);
    } catch (error) {
      console.error('Video action failed:', error);
      alert('Failed to process video: ' + error.message);
    }
  };

  // Open modal with filtered data
  const openModal = (type) => {
    let data = [];
    switch(type) {
      case 'pending_video':
        data = videoQueue;
        break;
      case 'request_edits':
        data = allSubmissions.filter(s => s.submission_status === 'changes_requested');
        break;
      case 'production_complete':
        data = allSubmissions.filter(s => s.video_status === 'approved');
        break;
      case 'storyboard_completed':
        // All approved storyboards (regardless of video status)
        data = allSubmissions.filter(s => s.submission_status === 'approved');
        break;
      case 'awaiting_video':
        // Approved storyboards without video submission
        data = allSubmissions.filter(s => s.submission_status === 'approved' && !s.video_status);
        break;
      default:
        data = [];
    }
    setModalData(data);
    setActiveModal(type);
  };

  const stats = supervisorStats?.summary || {};
  const executors = supervisorStats?.executors || [];
  
  // Calculate awaiting video count (approved storyboards without video)
  const awaitingVideoCount = allSubmissions.filter(s => s.submission_status === 'approved' && !s.video_status).length;

  return (
    <>
      {/* Group Selector — only shown when supervisor is in 2+ groups */}
      {userGroups.length > 1 && (
        <div
          data-testid="supervisor-group-selector"
          className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3 py-2 mb-2 shadow-sm"
        >
          <div className="flex items-center gap-2">
            <span className="text-base">👥</span>
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
              Group Scope
            </span>
          </div>
          <select
            data-testid="supervisor-group-select"
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            className="text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <option value="ALL">All My Groups ({userGroups.length})</option>
            {userGroups.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>
      )}

      {/* Supervisor Stats Cards - 5 Key Metrics */}
      {/* Row 1: 3 columns - Action items */}
      <div className="flex gap-2 mb-2" data-testid="supervisor-dashboard">
        {/* Pending Storyboard */}
        <Card 
          className="flex-1 bg-white border-0 hover:shadow-lg transition-all cursor-pointer active:scale-95 rounded-xl"
          style={{boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'}}
          onClick={() => navigate('/queue')}
        >
          <CardContent className="p-2 text-center">
            <div className="w-9 h-9 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-1">
              <span className="text-base">⏳</span>
            </div>
            <div className="text-xl font-black text-yellow-500">
              {stats.pending_approval || approvalQueue.length || 0}
            </div>
            <div className="text-[9px] text-gray-500 font-semibold leading-tight">Pending<br/>Storyboard</div>
          </CardContent>
        </Card>

        {/* Videos to Review */}
        <Card 
          className="flex-1 bg-white border-0 hover:shadow-lg transition-all cursor-pointer active:scale-95 rounded-xl"
          style={{boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'}}
          onClick={() => openModal('pending_video')}
        >
          <CardContent className="p-2 text-center">
            <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-1">
              <span className="text-base">📹</span>
            </div>
            <div className="text-xl font-black text-purple-500">
              {stats.videos_pending || videoQueue.length || 0}
            </div>
            <div className="text-[9px] text-gray-500 font-semibold leading-tight">Videos to<br/>Review</div>
          </CardContent>
        </Card>

        {/* Requesting Edits */}
        <Card 
          className="flex-1 bg-white border-0 hover:shadow-lg transition-all cursor-pointer active:scale-95 rounded-xl"
          style={{boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'}}
          onClick={() => openModal('request_edits')}
        >
          <CardContent className="p-2 text-center">
            <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-1">
              <span className="text-base">✏️</span>
            </div>
            <div className="text-xl font-black text-orange-500">
              {stats.request_changes || 0}
            </div>
            <div className="text-[9px] text-gray-500 font-semibold leading-tight">Requesting<br/>Edits</div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: 2 columns - Key metrics */}
      <div className="flex gap-2 mb-3">
        {/* Awaiting Video */}
        <Card 
          className="flex-1 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 hover:shadow-lg transition-all cursor-pointer active:scale-95 rounded-xl"
          onClick={() => openModal('awaiting_video')}
        >
          <CardContent className="p-3 text-center">
            <div className="w-11 h-11 bg-blue-500 rounded-xl flex items-center justify-center mx-auto mb-1.5 shadow-md">
              <span className="text-lg text-white">🎬</span>
            </div>
            <div className="text-2xl font-black text-blue-600">
              {stats.awaiting_video || awaitingVideoCount || 0}
            </div>
            <div className="text-[10px] text-blue-700 font-bold">Awaiting Video</div>
            <div className="text-[8px] text-blue-500">From Executor</div>
          </CardContent>
        </Card>

        {/* Production Complete */}
        <Card 
          className="flex-1 bg-gradient-to-br from-green-50 to-emerald-100 border border-green-200 hover:shadow-lg transition-all cursor-pointer active:scale-95 rounded-xl"
          onClick={() => openModal('production_complete')}
        >
          <CardContent className="p-3 text-center">
            <div className="w-11 h-11 bg-green-500 rounded-xl flex items-center justify-center mx-auto mb-1.5 shadow-md">
              <span className="text-lg text-white">🎉</span>
            </div>
            <div className="text-2xl font-black text-green-600">
              {stats.videos_approved || 0}
            </div>
            <div className="text-[10px] text-green-700 font-bold">Production Done</div>
            <div className="text-[8px] text-green-500">Ready to Publish</div>
          </CardContent>
        </Card>
      </div>

      {/* Team SOA Overview */}
      <Card data-testid="team-soa-overview" className="bg-white border-0 shadow-sm mb-3">
        <CardHeader
          className="px-3 py-2 cursor-pointer"
          onClick={() => setSoaExpanded(!soaExpanded)}
        >
          <CardTitle className="text-sm text-slate-800 flex items-center justify-between font-semibold">
            <span className="flex items-center gap-2">
              🎯 Team SOA Performance
              <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0">
                {soaOverview?.totals?.drills_count || 0} drills
              </Badge>
            </span>
            <span className="text-xs text-slate-400">{soaExpanded ? '▴' : '▾'}</span>
          </CardTitle>
        </CardHeader>
        {soaExpanded && (
          <CardContent className="px-3 pb-3 pt-0">
            {/* Date range filter */}
            <div className="flex gap-2 mb-2 items-center">
              <input
                data-testid="soa-start-date"
                type="date" value={soaStart} max={soaEnd}
                onChange={(e) => setSoaStart(e.target.value)}
                className="flex-1 text-xs px-2 py-1.5 border border-slate-200 rounded"
              />
              <span className="text-xs text-slate-500">to</span>
              <input
                data-testid="soa-end-date"
                type="date" value={soaEnd} min={soaStart}
                onChange={(e) => setSoaEnd(e.target.value)}
                className="flex-1 text-xs px-2 py-1.5 border border-slate-200 rounded"
              />
              <button
                data-testid="soa-reload-btn"
                onClick={loadSoaOverview}
                disabled={soaLoading}
                className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded font-semibold disabled:opacity-50"
              >
                {soaLoading ? '...' : 'Apply'}
              </button>
            </div>

            {/* Quick ranges */}
            <div className="flex gap-1 mb-2">
              {[
                { label: 'Today', d: 0 },
                { label: '7d', d: 7 },
                { label: '30d', d: 30 },
                { label: '90d', d: 90 },
              ].map((r) => (
                <button
                  key={r.label}
                  onClick={() => {
                    const end = new Date().toISOString().split('T')[0];
                    const start = new Date(Date.now() - r.d * 86400000).toISOString().split('T')[0];
                    setSoaStart(start); setSoaEnd(end);
                  }}
                  className="text-[10px] px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded"
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* Totals row */}
            {soaOverview && (
              <div className="grid grid-cols-4 gap-2 mb-2">
                <div className="bg-slate-50 rounded p-1.5 text-center">
                  <div className="text-[14px] font-bold text-slate-800">{soaOverview.totals.drills_count}</div>
                  <div className="text-[9px] text-slate-500">Drills</div>
                </div>
                <div className="bg-slate-50 rounded p-1.5 text-center">
                  <div className="text-[14px] font-bold text-blue-600">{soaOverview.totals.avg_score}</div>
                  <div className="text-[9px] text-slate-500">Avg Score</div>
                </div>
                <div className="bg-slate-50 rounded p-1.5 text-center">
                  <div className="text-[14px] font-bold text-green-600">{soaOverview.totals.pass_rate}%</div>
                  <div className="text-[9px] text-slate-500">Pass Rate</div>
                </div>
                <div className="bg-slate-50 rounded p-1.5 text-center">
                  <div className="text-[14px] font-bold text-amber-600">{soaOverview.totals.total_bp}</div>
                  <div className="text-[9px] text-slate-500">Total BP</div>
                </div>
              </div>
            )}

            {/* Per-user table */}
            {soaLoading ? (
              <div className="text-center text-xs text-slate-500 py-4">Loading…</div>
            ) : (soaOverview?.users?.length ? (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {soaOverview.users.map((u, idx) => (
                  <div
                    key={u.user_id}
                    data-testid={`soa-user-row-${idx}`}
                    className={`border border-slate-200 rounded p-2 ${soaSelectedUser === u.user_id ? 'bg-blue-50' : 'bg-white'}`}
                  >
                    <div
                      className="flex justify-between items-center cursor-pointer"
                      onClick={() => setSoaSelectedUser(soaSelectedUser === u.user_id ? null : u.user_id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-slate-800 truncate">{u.name}</div>
                        <div className="text-[9px] text-slate-500">{u.drills_count} drills · {u.pass_rate}% pass</div>
                      </div>
                      <div className="text-right ml-2">
                        <div className={`text-sm font-bold ${u.avg_score >= 8 ? 'text-green-600' : u.avg_score >= 6 ? 'text-amber-600' : 'text-red-600'}`}>{u.avg_score}</div>
                        <div className="text-[9px] text-slate-400">{u.total_bp} BP</div>
                      </div>
                    </div>

                    {/* BP Breakdown — 5 metrics grid */}
                    <div
                      data-testid={`soa-user-bp-breakdown-${idx}`}
                      className="mt-1.5 grid grid-cols-5 gap-1 text-center"
                    >
                      <div className="bg-purple-50 rounded px-1 py-1 border border-purple-100">
                        <div className="text-[11px] font-bold text-purple-700">{u.ai_rescue_count ?? 0}</div>
                        <div className="text-[8px] text-purple-500 leading-tight">AI Rescue<br/>Used</div>
                      </div>
                      <div className="bg-red-50 rounded px-1 py-1 border border-red-100">
                        <div className="text-[11px] font-bold text-red-600">{u.penalty_rate ?? 0}%</div>
                        <div className="text-[8px] text-red-500 leading-tight">Penalty<br/>Rate</div>
                      </div>
                      <div className="bg-slate-50 rounded px-1 py-1 border border-slate-200">
                        <div className="text-[11px] font-bold text-slate-700">{u.base_bp_earned ?? 0}</div>
                        <div className="text-[8px] text-slate-500 leading-tight">Base BP<br/>Earned</div>
                      </div>
                      <div className="bg-orange-50 rounded px-1 py-1 border border-orange-100">
                        <div className="text-[11px] font-bold text-orange-600">−{u.penalty_deduction ?? 0}</div>
                        <div className="text-[8px] text-orange-500 leading-tight">Penalty<br/>Deduction</div>
                      </div>
                      <div className="bg-green-50 rounded px-1 py-1 border border-green-100">
                        <div className="text-[11px] font-bold text-green-700">{u.final_bp_awarded ?? u.total_bp ?? 0}</div>
                        <div className="text-[8px] text-green-600 leading-tight">Final BP<br/>Awarded</div>
                      </div>
                    </div>

                    {soaSelectedUser === u.user_id && soaOverview.drills && (
                      <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                        {soaOverview.drills.filter(d => d.user_id === u.user_id).slice(0, 10).map((d) => (
                          <div key={d.session_id} className="flex justify-between items-center text-[10px] py-0.5">
                            <span className="text-slate-600 truncate flex-1">{d.project_name || d.session_id}</span>
                            <span className={`font-bold mx-2 ${d.avg_score >= 8 ? 'text-green-600' : 'text-amber-600'}`}>{d.avg_score}</span>
                            {d.ai_rescues > 0 && <span className="text-purple-600">🛟×{d.ai_rescues}</span>}
                            <span className="text-slate-400 ml-2">{d.finalized_at ? new Date(d.finalized_at).toLocaleDateString() : '-'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-xs text-slate-500 py-4">
                Tiada drill dalam tempoh ini. Pilih tempoh lebih luas.
              </div>
            ))}
          </CardContent>
        )}
      </Card>

      {/* Pending Video Queue Section */}
      {videoQueue.length > 0 && (
        <Card className="bg-purple-600 border-0 shadow-xl mb-3">
          <CardHeader className="px-4 py-3">
            <CardTitle className="text-lg text-white flex items-center font-semibold">
              🎬 Videos Pending Review
              <Badge className="ml-2 bg-white text-purple-600 font-bold text-xs px-2 py-0.5">
                {videoQueue.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 py-3">
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {videoQueue.map((video) => (
                <div key={video.session_id} className="bg-white rounded-xl p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-purple-600 truncate">
                        {video.objective_type || 'Content'} • {video.executor_name || video.user_id?.slice(0, 8) || 'Executor'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 truncate">
                        <a href={video.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                          {video.video_url?.slice(0, 40)}...
                        </a>
                      </div>
                      {video.video_notes && (
                        <div className="text-xs text-gray-600 mt-1 italic">
                          &ldquo;{video.video_notes}&rdquo;
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button
                        size="sm"
                        onClick={() => window.open(video.video_url, '_blank')}
                        className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 h-7"
                      >
                        👁️
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleVideoAction(video.session_id, 'approve')}
                        className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 h-7"
                      >
                        ✓
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs px-2 py-1 h-7 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                        onClick={() => {
                          const reason = prompt('Rejection reason:');
                          if (reason) handleVideoAction(video.session_id, 'reject', reason);
                        }}
                      >
                        ✗
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Storyboard Queue Section */}
      <Card className="bg-emzi-blue border-0 shadow-xl">
        <CardHeader className="px-4 py-3">
          <CardTitle className="text-lg text-white flex items-center font-semibold">
            📋 Storyboard Queue
            <Badge className="ml-2 bg-white text-emzi-blue font-bold text-xs px-2 py-0.5">
              {approvalQueue.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 py-3">
          {approvalQueue.length === 0 ? (
            <div className="text-center py-6">
              <div className="text-white text-4xl mb-3">✅</div>
              <p className="text-white font-medium">All clear! No storyboards pending.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {approvalQueue.slice(0, 5).map((item) => {
                const itemId = item.submission_id || item.session_id;
                const title = txt(item.title || item.objective_type || item.project_name, 'en', 'Submission');
                const executorName = item.executor_name || item.user_id || 'Executor';
                const format = item.selected_format?.format_name || '';
                
                return (
                  <div key={itemId} className="bg-white rounded-xl p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-emzi-blue truncate">{title}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {executorName} {format && <span>• {format}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button
                          size="sm"
                          onClick={() => navigate(`/queue/review/${item.session_id || itemId}`)}
                          className="bg-emzi-blue hover:bg-blue-700 text-white text-xs px-2 py-1 h-7"
                        >
                          👁️
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApprovalAction(item, 'approve')}
                          className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 h-7"
                          disabled={loadingQueue}
                        >
                          ✓
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-2 py-1 h-7 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          onClick={() => {
                            const note = prompt('Rejection reason / Edit notes:');
                            if (note) handleApprovalAction(item, 'reject', note);
                          }}
                          disabled={loadingQueue}
                        >
                          ✗
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {approvalQueue.length > 0 && (
            <Button 
              onClick={() => navigate('/queue')} 
              className="w-full mt-4 bg-white text-emzi-blue hover:bg-gray-50 font-bold py-2 rounded-xl"
            >
              View All Queue Items →
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Storyboard Completed Section — approved submissions awaiting video / done */}
      {(() => {
        const completedItems = (allSubmissions || []).filter(
          (s) => s.submission_status === 'approved'
        );
        return (
          <Card
            data-testid="storyboard-completed-card"
            className="mt-3 bg-gradient-to-br from-green-600 to-emerald-700 border-0 shadow-xl"
          >
            <CardHeader className="px-4 py-3">
              <CardTitle className="text-lg text-white flex items-center font-semibold">
                ✅ Storyboard Completed
                <Badge className="ml-2 bg-white text-green-700 font-bold text-xs px-2 py-0.5">
                  {completedItems.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 py-3">
              {completedItems.length === 0 ? (
                <div className="text-center py-6">
                  <div className="text-white text-4xl mb-3">📭</div>
                  <p className="text-white font-medium">No completed storyboards yet.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-48 overflow-y-auto" data-testid="storyboard-completed-list">
                  {completedItems.slice(0, 5).map((item, idx) => {
                    const itemId = item.submission_id || item.session_id;
                    const title = txt(item.project_name || item.title || item.objective_type, 'en', 'Submission');
                    const executorName = item.executor_name || item.user_id || 'Executor';
                    const format = item.format_name || '';
                    const approvedAt = item.decided_at
                      ? new Date(item.decided_at).toLocaleDateString()
                      : '';
                    const videoStatus = item.video_status;
                    const videoBadge =
                      videoStatus === 'approved'
                        ? { text: 'Video Approved', cls: 'bg-emerald-100 text-emerald-700' }
                        : videoStatus === 'pending_review'
                        ? { text: 'Video Pending Review', cls: 'bg-amber-100 text-amber-700' }
                        : videoStatus === 'rejected'
                        ? { text: 'Video Rejected', cls: 'bg-red-100 text-red-700' }
                        : { text: 'Awaiting Video', cls: 'bg-blue-100 text-blue-700' };
                    return (
                      <div
                        key={itemId}
                        data-testid={`storyboard-completed-item-${idx}`}
                        className="bg-white rounded-xl p-3"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-green-700 truncate">{title}</div>
                            <div className="text-xs text-gray-600 mt-1">
                              {executorName}
                              {format && <span> • {format}</span>}
                              {approvedAt && <span> • Approved {approvedAt}</span>}
                            </div>
                            <div className="mt-1">
                              <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${videoBadge.cls}`}>
                                {videoBadge.text}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <Button
                              size="sm"
                              onClick={() =>
                                navigate(`/queue/review/${item.session_id || itemId}`)
                              }
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 h-7"
                              data-testid={`storyboard-completed-view-${idx}`}
                            >
                              👁️
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {completedItems.length > 5 && (
                <Button
                  onClick={() => openModal('storyboard_completed')}
                  className="w-full mt-4 bg-white text-green-700 hover:bg-gray-50 font-bold py-2 rounded-xl"
                  data-testid="storyboard-completed-view-all"
                >
                  View All {completedItems.length} Completed →
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Executor Performance Tracker - New Redesigned Component */}
      {executors.length > 0 && (
        <ExecutorPerformanceTracker 
          executors={executors}
          onExecutorClick={(executor) => console.log('Executor clicked:', executor)}
        />
      )}

      {/* Video Approval Comparison Chart - Trend Analysis */}
      <VideoApprovalComparison />

      {/* Video Monthly Trend Chart - 6 Month Overview */}
      <VideoMonthlyTrend />

      {/* Detail Modal for each category */}
      {activeModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50"
          onClick={() => setActiveModal(null)}
        >
          <div 
            className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[85vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`px-4 py-4 border-b flex items-center justify-between ${
              activeModal === 'pending_video' ? 'bg-purple-500 text-white' :
              activeModal === 'request_edits' ? 'bg-orange-500 text-white' :
              activeModal === 'production_complete' ? 'bg-green-500 text-white' :
              activeModal === 'storyboard_completed' ? 'bg-gradient-to-r from-green-600 to-emerald-700 text-white' :
              'bg-blue-500 text-white'
            }`}>
              <div>
                <h3 className="font-bold text-lg">
                  {activeModal === 'pending_video' && '🎬 Pending Video Review'}
                  {activeModal === 'request_edits' && '📝 Requesting Edits'}
                  {activeModal === 'production_complete' && '✅ Production Complete'}
                  {activeModal === 'storyboard_completed' && '✅ Storyboard Completed'}
                  {activeModal === 'awaiting_video' && '📋 Awaiting Video Submission'}
                </h3>
                <p className="text-sm opacity-90">{modalData.length} item(s)</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setActiveModal(null)}
                className="text-white hover:bg-white hover:bg-opacity-20"
              >
                ✕
              </Button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {modalData.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">📭</div>
                  <p className="text-gray-500 font-medium">No items found</p>
                </div>
              ) : (
                modalData.map((item) => (
                  <Card key={item.session_id} className="border shadow-sm hover:shadow-md transition-all">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Submission Title & Status */}
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-bold text-gray-800 text-sm">
                              {item.project_name || `${item.objective_type || 'Storyboard'} - ${item.submitted_at ? new Date(item.submitted_at).toLocaleDateString('ms-MY', { day: '2-digit', month: 'short' }) : 'N/A'}`}
                            </span>
                            {item.submission_status === 'approved' && (
                              <Badge className="bg-green-100 text-green-700 text-xs">✅ Approved</Badge>
                            )}
                            {item.submission_status === 'changes_requested' && (
                              <Badge className="bg-orange-100 text-orange-700 text-xs">✏️ Edit Requested</Badge>
                            )}
                            {item.video_status === 'pending_review' && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">🎬 Video Pending</Badge>
                            )}
                            {item.video_status === 'approved' && (
                              <Badge className="bg-green-100 text-green-700 text-xs">🎉 Complete</Badge>
                            )}
                          </div>

                          {/* Executor & Format */}
                          <div className="text-xs text-gray-600 mb-1">
                            👤 {item.executor_name || 'Executor'} • 
                            🎬 {item.format_name || 'Unknown Format'} • 
                            📅 {item.submitted_at ? new Date(item.submitted_at).toLocaleString('ms-MY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                          </div>

                          {/* Video URL if exists */}
                          {item.video_url && (
                            <div className="mt-2 text-xs bg-purple-50 p-2 rounded">
                              🔗 <a href={item.video_url} target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">
                                {item.video_url.slice(0, 45)}...
                              </a>
                              {item.video_notes && (
                                <p className="mt-1 text-gray-600 italic">&ldquo;{item.video_notes}&rdquo;</p>
                              )}
                            </div>
                          )}

                          {/* Supervisor Comment if exists */}
                          {item.supervisor_comment && (
                            <div className="mt-2 text-xs bg-blue-50 p-2 rounded text-blue-700">
                              💬 <strong>Your comment:</strong> {item.supervisor_comment}
                            </div>
                          )}

                          {/* Storyboard info */}
                          {item.storyboard_beats_count && (
                            <div className="mt-1 text-xs text-gray-500">
                              📝 {item.storyboard_beats_count} beats • {item.format_name || 'Standard format'}
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-1">
                          {/* View Storyboard */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setActiveModal(null);
                              navigate(`/queue/review/${item.session_id}`);
                            }}
                            className="text-xs h-7 px-2"
                          >
                            👁️ View
                          </Button>

                          {/* Video Review Actions */}
                          {activeModal === 'pending_video' && item.video_url && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => window.open(item.video_url, '_blank')}
                                className="bg-blue-500 hover:bg-blue-600 text-white text-xs h-7 px-2"
                              >
                                🎬 Watch
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleVideoAction(item.session_id, 'approve')}
                                className="bg-green-500 hover:bg-green-600 text-white text-xs h-7 px-2"
                              >
                                ✓ Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7 px-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                onClick={() => {
                                  const reason = prompt('Rejection reason:');
                                  if (reason) handleVideoAction(item.session_id, 'reject', reason);
                                }}
                              >
                                ✗ Reject
                              </Button>
                            </>
                          )}

                          {/* Production Complete - View video */}
                          {activeModal === 'production_complete' && item.video_url && (
                            <Button
                              size="sm"
                              onClick={() => window.open(item.video_url, '_blank')}
                              className="bg-green-500 hover:bg-green-600 text-white text-xs h-7 px-2"
                            >
                              🎬 Watch
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50">
              <Button 
                onClick={() => setActiveModal(null)}
                variant="outline"
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================
// EXECUTOR DASHBOARD CONTENT - Enhanced with Interactive Modals
// ============================================================
const ExecutorDashboardContent = ({ user, navigate, icons, t, studios }) => {
  const { token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [activeModal, setActiveModal] = useState(null); // 'all', 'approved', 'pending', 'production_done', 'needs_video'
  const [modalData, setModalData] = useState([]);
  
  // Video submission modal
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoNotes, setVideoNotes] = useState('');
  const [videoSubmitting, setVideoSubmitting] = useState(false);

  useEffect(() => {
    loadMySubmissions();
  }, []);

  const loadMySubmissions = async () => {
    try {
      setLoading(true);
      const response = await contentStudioApi.getMySubmissions(token, user?.user_id);
      setSubmissions(response?.submissions || []);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  // Note: Backend now uses 'pending_approval' as standardized status for all pending submissions
  const stats = {
    total: submissions.length,
    pending: submissions.filter(s => s.submission_status === 'pending_approval').length,
    approved: submissions.filter(s => s.submission_status === 'approved' && s.video_status !== 'approved').length,
    changesRequested: submissions.filter(s => s.submission_status === 'changes_requested').length,
    videoPending: submissions.filter(s => s.video_status === 'pending_review').length,
    productionDone: submissions.filter(s => s.video_status === 'approved').length,
    needsVideoSubmission: submissions.filter(s => s.submission_status === 'approved' && !s.video_status).length
  };

  // Open modal with filtered data
  const openModal = (type) => {
    let data = [];
    switch(type) {
      case 'all':
        data = submissions;
        break;
      case 'approved':
        data = submissions.filter(s => s.submission_status === 'approved' && s.video_status !== 'approved');
        break;
      case 'pending':
        // Standardized: all pending submissions use 'pending_approval'
        data = submissions.filter(s => s.submission_status === 'pending_approval');
        break;
      case 'changes_requested':
        data = submissions.filter(s => s.submission_status === 'changes_requested');
        break;
      case 'production_done':
        data = submissions.filter(s => s.video_status === 'approved');
        break;
      case 'needs_video':
        data = submissions.filter(s => s.submission_status === 'approved' && !s.video_status);
        break;
      default:
        data = [];
    }
    setModalData(data);
    setActiveModal(type);
  };

  const handleOpenVideoModal = (submission) => {
    setSelectedSubmission(submission);
    setVideoUrl(submission.video_url || '');
    setVideoNotes(submission.video_notes || '');
    setShowVideoModal(true);
    setActiveModal(null); // Close the list modal
  };

  const handleSubmitVideo = async () => {
    if (!videoUrl.trim()) {
      alert('Please enter a video URL');
      return;
    }
    
    if (!videoUrl.startsWith('http://') && !videoUrl.startsWith('https://')) {
      alert('Please enter a valid URL starting with http:// or https://');
      return;
    }

    try {
      setVideoSubmitting(true);
      await contentStudioApi.submitVideo(token, selectedSubmission.session_id, videoUrl, videoNotes);
      alert('✅ Video submitted for review!');
      setShowVideoModal(false);
      setVideoUrl('');
      setVideoNotes('');
      loadMySubmissions();
    } catch (error) {
      console.error('Failed to submit video:', error);
      alert('Failed to submit video: ' + error.message);
    } finally {
      setVideoSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_approval':
        return <Badge className="bg-yellow-100 text-yellow-700 text-xs">⏳ Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700 text-xs">✅ Approved</Badge>;
      case 'changes_requested':
        return <Badge className="bg-orange-100 text-orange-700 text-xs">✏️ Edit Requested</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 text-xs">❌ Rejected</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700 text-xs">{status}</Badge>;
    }
  };

  const getVideoStatusBadge = (status) => {
    switch (status) {
      case 'pending_review':
        return <Badge className="bg-purple-100 text-purple-700 text-xs">🎬 Video Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700 text-xs">🎉 Complete</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 text-xs">🔄 Video Rejected</Badge>;
      default:
        return null;
    }
  };

  const getModalConfig = (type) => {
    const configs = {
      'all': { title: '📊 All Submissions', color: 'bg-emzi-blue' },
      'approved': { title: '✅ Approved Storyboards', color: 'bg-green-500' },
      'pending': { title: '⏳ Pending Review', color: 'bg-yellow-500' },
      'changes_requested': { title: '✏️ Edit Requested', color: 'bg-orange-500' },
      'production_done': { title: '🎉 Production Complete', color: 'bg-purple-500' },
      'needs_video': { title: '📹 Ready for Video', color: 'bg-green-500' }
    };
    return configs[type] || { title: 'Submissions', color: 'bg-gray-500' };
  };

  return (
    <>
      {/* Performance Stats - Compact 2x2 Grid (Matching Supervisor Style) */}
      {/* Row 1: 2 columns */}
      <div className="flex gap-2 mb-2" data-testid="executor-dashboard">
        {/* Total Submissions */}
        <Card 
          className="flex-1 bg-white border-0 hover:shadow-lg transition-all cursor-pointer active:scale-95 rounded-xl" 
          style={{boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'}}
          onClick={() => openModal('all')}
        >
          <CardContent className="p-3 text-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-1">
              <span className="text-lg">📋</span>
            </div>
            <div className="text-2xl font-black text-emzi-blue">{stats.total}</div>
            <div className="text-[10px] text-gray-500 font-semibold">Total Submissions</div>
          </CardContent>
        </Card>

        {/* Approved */}
        <Card 
          className="flex-1 bg-white border-0 hover:shadow-lg transition-all cursor-pointer active:scale-95 rounded-xl" 
          style={{boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'}}
          onClick={() => openModal('approved')}
        >
          <CardContent className="p-3 text-center">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-1">
              <span className="text-lg">✅</span>
            </div>
            <div className="text-2xl font-black text-green-500">{stats.approved}</div>
            <div className="text-[10px] text-gray-500 font-semibold">Approved</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Row 2: 2 columns */}
      <div className="flex gap-2 mb-2">
        {/* Pending Review */}
        <Card 
          className="flex-1 bg-white border-0 hover:shadow-lg transition-all cursor-pointer active:scale-95 rounded-xl" 
          style={{boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'}}
          onClick={() => openModal('pending')}
        >
          <CardContent className="p-3 text-center">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-1">
              <span className="text-lg">⏳</span>
            </div>
            <div className="text-2xl font-black text-yellow-500">{stats.pending}</div>
            <div className="text-[10px] text-gray-500 font-semibold">Pending Review</div>
          </CardContent>
        </Card>

        {/* Production Complete */}
        <Card 
          className="flex-1 bg-white border-0 hover:shadow-lg transition-all cursor-pointer active:scale-95 rounded-xl" 
          style={{boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'}}
          onClick={() => openModal('production_done')}
        >
          <CardContent className="p-3 text-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-1">
              <span className="text-lg">🎬</span>
            </div>
            <div className="text-2xl font-black text-purple-500">{stats.productionDone}</div>
            <div className="text-[10px] text-gray-500 font-semibold">Production Done</div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Cards - Compact Row */}
      {(stats.needsVideoSubmission > 0 || stats.changesRequested > 0) && (
        <div className="flex gap-2">
          {/* Needs Video Submission */}
          {stats.needsVideoSubmission > 0 && (
            <Card 
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 border-0 shadow-lg cursor-pointer hover:shadow-xl transition-all active:scale-98 rounded-xl"
              onClick={() => openModal('needs_video')}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between text-white">
                  <div>
                    <div className="text-sm font-bold">🎉 {stats.needsVideoSubmission} Ready!</div>
                    <div className="text-[10px] opacity-90">Submit video</div>
                  </div>
                  <div className="text-2xl">📹</div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Edit Requested Alert */}
          {stats.changesRequested > 0 && (
            <Card 
              className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 border-0 shadow-lg cursor-pointer hover:shadow-xl transition-all active:scale-98 rounded-xl"
              onClick={() => openModal('changes_requested')}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between text-white">
                  <div>
                    <div className="text-sm font-bold">✏️ {stats.changesRequested} Revisions</div>
                    <div className="text-[10px] opacity-90">Edit needed</div>
                  </div>
                  <div className="text-2xl">📝</div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Creative Studios - Compact 2x2 Grid */}
      <Card className="bg-emzi-blue border-0 shadow-lg rounded-xl">
        <CardHeader className="px-3 py-2">
          <CardTitle className="text-sm text-white flex items-center font-semibold">
            🎬 Creative Studios
            <Badge className="ml-2 bg-white text-emzi-blue font-bold text-[10px] px-1.5 py-0.5">
              {studios?.filter(s => s.ready).length || 0}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 py-2">
          <div className="flex flex-wrap gap-2">
            {studios?.map((studio) => (
              <div key={studio.id} className="w-[calc(50%-4px)]">
                <GridTile
                  icon={studio.icon}
                  label={studio.name}
                  to={studio.path}
                  locked={!studio.ready}
                  testId={`studio-${studio.id}-button`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions - Compact */}
      <div className="grid grid-cols-2 gap-2">
        <Button 
          onClick={() => openModal('all')}
          className="bg-emzi-blue hover:bg-blue-700 text-white font-bold py-2 text-sm rounded-xl"
        >
          📋 All Submissions
        </Button>
        <Button 
          onClick={() => navigate('/production-mode')}
          className={`font-bold py-2 text-sm rounded-xl ${
            stats.needsVideoSubmission > 0 
              ? 'bg-green-500 hover:bg-green-600 text-white animate-pulse' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
          }`}
        >
          🎬 Production {stats.needsVideoSubmission > 0 && `(${stats.needsVideoSubmission})`}
        </Button>
      </div>

      {/* Detail Modal for submissions */}
      {activeModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50"
          onClick={() => setActiveModal(null)}
        >
          <div 
            className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[85vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`px-4 py-4 border-b flex items-center justify-between ${getModalConfig(activeModal).color} text-white`}>
              <div>
                <h3 className="font-bold text-lg">{getModalConfig(activeModal).title}</h3>
                <p className="text-sm opacity-90">{modalData.length} item(s)</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setActiveModal(null)}
                className="text-white hover:bg-white hover:bg-opacity-20"
              >
                ✕
              </Button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-3 border-emzi-blue border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading...</p>
                </div>
              ) : modalData.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">📭</div>
                  <p className="text-gray-500 font-medium">No items found</p>
                  <Button 
                    onClick={() => { setActiveModal(null); navigate('/studios'); }}
                    className="mt-4 bg-emzi-blue"
                  >
                    Create Content →
                  </Button>
                </div>
              ) : (
                modalData.map((sub) => (
                  <Card key={sub.session_id} className="border shadow-sm hover:shadow-md transition-all">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Submission Title & Status */}
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-bold text-gray-800 text-sm">{sub.project_name || `${sub.objective_type || 'Storyboard'} - ${sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString('ms-MY', { day: '2-digit', month: 'short' }) : 'Draft'}`}</span>
                            {getStatusBadge(sub.submission_status)}
                            {sub.video_status && getVideoStatusBadge(sub.video_status)}
                          </div>

                          {/* Details */}
                          <div className="text-xs text-gray-600 mb-1">
                            🎬 {sub.format_name || 'Unknown Format'} • 
                            📅 {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString('ms-MY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                          </div>

                          {/* Format & Beats */}
                          {sub.storyboard_beats_count > 0 && (
                            <div className="text-xs text-gray-500">
                              📝 {sub.storyboard_beats_count} beats
                            </div>
                          )}

                          {/* Supervisor Comment */}
                          {sub.supervisor_comment && (
                            <div className="mt-2 text-xs bg-blue-50 p-2 rounded text-blue-700">
                              💬 <strong>Supervisor:</strong> {sub.supervisor_comment}
                            </div>
                          )}

                          {/* Video URL */}
                          {sub.video_url && (
                            <div className="mt-2 text-xs bg-purple-50 p-2 rounded">
                              🔗 <a href={sub.video_url} target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">
                                View Video
                              </a>
                              {sub.video_status === 'rejected' && sub.video_rejection_reason && (
                                <p className="mt-1 text-red-600">❌ {sub.video_rejection_reason}</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-1">
                          {/* View Storyboard */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setActiveModal(null);
                              navigate('/my-submissions');
                            }}
                            className="text-xs h-7 px-2"
                          >
                            👁️ View
                          </Button>

                          {/* Submit Video - for approved without video */}
                          {sub.submission_status === 'approved' && !sub.video_status && (
                            <Button
                              size="sm"
                              onClick={() => handleOpenVideoModal(sub)}
                              className="bg-green-500 hover:bg-green-600 text-white text-xs h-7 px-2"
                            >
                              📹 Video
                            </Button>
                          )}

                          {/* Re-submit Video if rejected */}
                          {sub.video_status === 'rejected' && (
                            <Button
                              size="sm"
                              onClick={() => handleOpenVideoModal(sub)}
                              className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-7 px-2"
                            >
                              🔄 Re-submit
                            </Button>
                          )}

                          {/* Watch Video if complete */}
                          {sub.video_status === 'approved' && sub.video_url && (
                            <Button
                              size="sm"
                              onClick={() => window.open(sub.video_url, '_blank')}
                              className="bg-purple-500 hover:bg-purple-600 text-white text-xs h-7 px-2"
                            >
                              🎬 Watch
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50">
              <Button 
                onClick={() => setActiveModal(null)}
                variant="outline"
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Video Submission Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white">
            <CardHeader>
              <CardTitle className="text-lg">📹 Submit Final Video</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-gray-600 bg-green-50 p-3 rounded-lg">
                <p className="font-medium">🎉 {selectedSubmission?.project_name || `${selectedSubmission?.objective_type || 'Storyboard'} - ${selectedSubmission?.submitted_at ? new Date(selectedSubmission.submitted_at).toLocaleDateString('ms-MY', { day: '2-digit', month: 'short' }) : 'Approved'}`}</p>
                <p className="mt-1">Submit your final video link for supervisor review.</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video URL *
                </label>
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emzi-blue focus:border-emzi-blue"
                />
                <p className="text-xs text-gray-500 mt-1">
                  YouTube, TikTok, Telegram, Vimeo, or any public link
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={videoNotes}
                  onChange={(e) => setVideoNotes(e.target.value)}
                  placeholder="Any notes for the supervisor..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emzi-blue focus:border-emzi-blue"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowVideoModal(false);
                    setVideoUrl('');
                    setVideoNotes('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  onClick={handleSubmitVideo}
                  disabled={videoSubmitting || !videoUrl.trim()}
                >
                  {videoSubmitting ? '⏳ Submitting...' : '📤 Submit'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};
const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, authReady, isSupervisor, isManagement, isAdmin, isExecutor, getRoleName } = useAuth();
  const t = useLang();
  
  // Build version verification log
  console.log('🔷 Dashboard_Final mounted with GridTile v6.4-GT - Build: 2025-10-23T13:30Z');
  console.log('🔷 User role:', user?.role);
  console.log('🔷 GridTile component imported:', typeof GridTile !== 'undefined' ? '✅ YES' : '❌ NO');
  
  const [health, setHealth] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [approvalQueue, setApprovalQueue] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [managementStats, setManagementStats] = useState(null);

  // Redirect admins to Admin Control Panel
  useEffect(() => {
    if (authReady && isAdmin()) {
      navigate('/admin', { replace: true });
    }
  }, [authReady, isAdmin, navigate]);

  // Load dashboard data
  useEffect(() => {
    if (authReady && user?.role) {
      loadData();
    }
  }, [authReady, user?.role, user?.user_id]);

  const loadData = async () => {
    try {
      const promises = [
        nexusApi.health(),
        nexusApi.getScorecard(user?.user_id)
      ];
      
      if (isSupervisor() || isManagement() || isAdmin()) {
        promises.push(userManagementApi.getApprovalQueue());
      }
      
      // Load management stats for management roles
      if (isManagement() && !isAdmin()) {
        promises.push(userManagementApi.getManagementDashboardStats());
      }
      
      const results = await Promise.all(promises);
      setHealth(results[0]?.data);
      setScorecard(results[1]?.data);
      
      let resultIndex = 2;
      if (isSupervisor() || isManagement() || isAdmin()) {
        const queueData = results[resultIndex]?.queue || results[resultIndex]?.items || [];
        console.log('[Dashboard] Approval queue loaded:', queueData.length, 'items');
        setApprovalQueue(queueData);
        resultIndex++;
      }
      
      // Set management stats
      if (isManagement() && !isAdmin() && results[resultIndex]) {
        console.log('[Dashboard] Management stats loaded:', results[resultIndex]);
        setManagementStats(results[resultIndex]);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const handleApprovalAction = async (item, action, note = '', reason = '') => {
    setLoadingQueue(true);
    try {
      // Support both old format (string submissionId) and new format (full item object)
      const isItemObject = typeof item === 'object' && item !== null;
      const submissionId = isItemObject ? (item.submission_id || item.session_id) : item;
      const sessionId = isItemObject ? item.session_id : null;
      const source = isItemObject ? item.source : null;
      
      await userManagementApi.approvalAction({
        submission_id: submissionId,
        session_id: sessionId,
        source: source,
        action: action,
        note: note,
        reason: reason
      });
      
      const queueRes = await userManagementApi.getApprovalQueue();
      setApprovalQueue(queueRes?.queue || queueRes?.items || []);
    } catch (error) {
      console.error('Approval action failed:', error);
      alert('Failed to perform approval action. Please try again.');
    } finally {
      setLoadingQueue(false);
    }
  };

  // Loading state with EMZI branding
  if (!authReady || !user) {
    return (
      <div className="nexus-content flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-emzi-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-gray-600 font-medium">{t('dashboard.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  // If admin, they should be redirected - show nothing
  if (isAdmin()) {
    return null;
  }

  // Minimalist SVG Icons
  const icons = {
    brainPoints: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    level: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    system: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    content: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    live: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
      </svg>
    ),
    copy: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    analyst: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    submissions: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    queue: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    analytics: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    admin: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    announcements: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    ),
    whatsapp: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
    soaDrill: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    knowledgePack: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    campaign: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    )
  };

  // Environment detection — see /app/frontend/src/utils/environment.js
  const isProduction = isProductionHost();
  const isDevelopment = !isProduction;

  const studios = [
    { 
      id: 'content', 
      name: 'Content', 
      icon: icons.content, 
      path: '/studio/content-v2', 
      ready: true  // Always unlocked
    },
    { 
      id: 'live', 
      name: 'Live', 
      icon: icons.live, 
      path: '/studio/live-v2', 
      ready: isDevelopment  // Only unlocked in development
    },
    { 
      id: 'copy', 
      name: 'Copy', 
      icon: icons.copy, 
      path: '/studio/copy', 
      ready: isDevelopment  // Only unlocked in development
    },
    { 
      id: 'analyst', 
      name: 'Analyst', 
      icon: icons.analyst, 
      path: '/studio/analyst', 
      ready: isDevelopment  // Only unlocked in development
    }
  ];

  return (
    <div className="nexus-content overflow-hidden bg-white">
      
      {/* Unified EMZI NEXUS Header - Same design for all roles */}
      <NexusHeader onLogout={logout} />
      
      {/* Main Content - Ultra Compact Layout for 1-Page Fit */}
      <div className="flex-1 overflow-y-auto panel-scroll px-4 pt-3 pb-24 space-y-3">
        
        {/* EXECUTOR DASHBOARD */}
        {isExecutor() && !isSupervisor() && !isManagement() && !isAdmin() && (
          <ExecutorDashboardContent 
            user={user}
            navigate={navigate}
            icons={icons}
            t={t}
            studios={studios}
          />
        )}

        {/* SUPERVISOR DASHBOARD */}
        {isSupervisor() && !isAdmin() && (
          <SupervisorDashboardContent 
            user={user}
            approvalQueue={approvalQueue}
            loadingQueue={loadingQueue}
            handleApprovalAction={handleApprovalAction}
            navigate={navigate}
            icons={icons}
            t={t}
          />
        )}

        {/* MANAGEMENT DASHBOARD */}
        {isManagement() && !isAdmin() && (
          <>
            {/* Management Stats - Real Data from API - 2 Column */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <Card 
                className="bg-white border transition-all duration-200 touch-target" 
                style={{
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  boxShadow: EMZI_SHADOWS.sm,
                  minHeight: '70px'
                }}
              >
                <CardContent className="text-center" style={{ padding: '10px' }}>
                  <div style={{ color: EMZI_COLORS.primary, marginBottom: '4px' }}>
                    {icons.submissions}
                  </div>
                  <div 
                    style={{ 
                      fontSize: '22px', 
                      fontWeight: '700', 
                      color: EMZI_COLORS.primary,
                      marginBottom: '2px' 
                    }}
                  >
                    {managementStats?.total_submissions ?? '—'}
                  </div>
                  <div style={{ fontSize: '10px', color: EMZI_COLORS.textSecondary, fontWeight: '500' }}>
                    {t('dashboard.totalSubmissions')}
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="bg-white border transition-all duration-200 touch-target" 
                style={{
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  boxShadow: EMZI_SHADOWS.sm,
                  minHeight: '70px'
                }}
              >
                <CardContent className="text-center" style={{ padding: '10px' }}>
                  <div style={{ color: EMZI_COLORS.success, marginBottom: '4px' }}>
                    {icons.analytics}
                  </div>
                  <div 
                    style={{ 
                      fontSize: '22px', 
                      fontWeight: '700', 
                      color: EMZI_COLORS.success,
                      marginBottom: '2px' 
                    }}
                  >
                    {managementStats?.compliance_rate != null ? `${managementStats.compliance_rate}%` : '—'}
                  </div>
                  <div style={{ fontSize: '10px', color: EMZI_COLORS.textSecondary, fontWeight: '500' }}>
                    {t('dashboard.complianceRate')}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Management Center - Compact 2-Column Grid (No Queue) */}
            <Card 
              className="bg-emzi-blue border-0" 
              style={{
                boxShadow: '0 8px 32px rgba(0, 122, 255, 0.25), 0 4px 16px rgba(0, 0, 0, 0.15)'
              }}
            >
              <CardHeader className="px-4 py-2">
                <CardTitle className="text-base text-white flex items-center justify-between font-semibold">
                  <div className="flex items-center">
                    {t('dashboard.managementCenter')}
                    {managementStats?.user_groups?.length > 0 && (
                      <Badge 
                        className="ml-2 text-white border-0 font-bold text-xs px-2 py-0.5"
                        style={{ backgroundColor: '#F7941E' }}
                      >
                        {managementStats.user_groups.join(', ')}
                      </Badge>
                    )}
                  </div>
                  <div 
                    className="text-xs font-normal px-2 py-0.5 rounded"
                    style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                  >
                    v6.3.2
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 py-2">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <GridTile
                    icon={icons.analytics}
                    label={t('dashboard.analytics')}
                    to="/analytics"
                    testId="dashboard-tile-analytics"
                  />
                  <GridTile
                    icon={icons.campaign}
                    label="Campaigns"
                    to="/campaigns"
                    testId="dashboard-tile-campaigns"
                  />
                  <GridTile
                    icon={icons.knowledgePack}
                    label={t('dashboard.knowledgePacks')}
                    to="/knowledge-packs"
                    testId="dashboard-tile-knowledge-packs"
                  />
                  <GridTile
                    icon={<span>💬</span>}
                    label="Communications"
                    to="/communications?tab=inbox"
                    testId="dashboard-tile-communications"
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ADMIN DASHBOARD */}
        {isAdmin() && (
          <>
            {/* Admin Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="bg-white border-0 shadow-lg">
                <CardContent className="p-4 text-center">
                  <div className="text-purple-600 mb-2">👥</div>
                  <div className="text-2xl font-bold text-gray-900">All</div>
                  <div className="text-xs text-gray-600 font-medium">Users</div>
                </CardContent>
              </Card>
              <Card className="bg-white border-0 shadow-lg">
                <CardContent className="p-4 text-center">
                  <div className="text-green-600 mb-2">✓</div>
                  <div className="text-2xl font-bold text-gray-900">Admin</div>
                  <div className="text-xs text-gray-600 font-medium">Access</div>
                </CardContent>
              </Card>
              <Card className="bg-white border-0 shadow-lg">
                <CardContent className="p-4 text-center">
                  <div className="text-blue-600 mb-2">🔐</div>
                  <div className="text-2xl font-bold text-gray-900">Full</div>
                  <div className="text-xs text-gray-600 font-medium">Control</div>
                </CardContent>
              </Card>
            </div>

            {/* Communications Hub - For Admin */}
            <Card className="bg-white border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-gray-900 flex items-center font-bold">
                  <div className="text-emzi-blue mr-2">💬</div>
                  Communications Hub
                  <Badge className="ml-2 bg-blue-100 text-blue-800 font-bold">Inbox + Analytics</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-sm text-gray-700 mb-3">
                  Full access to inbox, analytics, approvals, and system communications
                </p>
                <Button
                  onClick={() => navigate('/communications?tab=inbox')}
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold hover:from-blue-600 hover:to-blue-700 border-0 shadow-md"
                  data-testid="communications-hub-button"
                >
                  📬 Open Inbox
                </Button>
              </CardContent>
            </Card>

            {/* Admin Panel Card */}
            <Card className="bg-purple-600 border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white flex items-center font-bold">
                  Admin Panel
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-sm text-white opacity-90 mb-3">
                  Manage users, view analytics, and configure system settings
                </p>
                <Button
                  onClick={() => navigate('/admin')}
                  className="w-full bg-white text-purple-600 hover:bg-gray-100 font-bold"
                >
                  Open Admin Panel
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;